import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyExpenseAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import './PropertyExpenseView.css';

const PropertyExpenseView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadExpense();
  }, [id]);

  const loadExpense = async () => {
    try {
      setLoading(true);
      const response = await propertyExpenseAPI.getById(id);
      const data = response.data;
      setExpense({
        ...data,
        date: data.date ? new Date(data.date).toISOString().split('T')[0] : '',
        amount: data.amount || '',
        expenseDescription: data.expenseDescription || '',
        invoiceNumber: data.invoiceNumber || '',
        payingEntity: data.payingEntity || '',
      });
    } catch (err) {
      setErrorMessage(t('propertyExpense.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const response = await propertyExpenseAPI.printSingle(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('propertyExpense.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-actions">
          <button className="btn-back" onClick={() => navigate('/property-expense')}>
            <i className="fas fa-arrow-left"></i> {t('propertyExpense.view.backToList')}
          </button>
          <button className="btn-edit" onClick={() => navigate(`/property-expense/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('propertyExpense.view.edit')}
          </button>
          <button className="btn-primary" onClick={handlePrint} style={{ marginLeft: '10px' }}>
            <i className="fas fa-print"></i> {t('common.print', 'Print')}
          </button>
        </div>

      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('propertyExpense.view.fields.date')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={expense.date}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-dollar-sign"></i> {t('propertyExpense.view.fields.amount')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-dollar-sign input-icon"></i>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={expense.amount}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-file-alt"></i> {t('propertyExpense.view.fields.expenseDescription')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-file-alt input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.expenseDescription || ''}
                readOnly
                disabled
              />
          </div>
        </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-receipt"></i> {t('propertyExpense.view.fields.invoiceNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-receipt input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.invoiceNumber || ''}
                readOnly
                disabled
              />
            </div>
            </div>
          </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-building"></i> {t('propertyExpense.view.fields.payingEntity')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-building input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.payingEntity || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default PropertyExpenseView;


